/**
 * Canonical timeline contract (matches backend TimelineItem).
 * Use normalizeTimelineItems() when consuming API responses.
 */

export const TIMELINE_TONES = {
  success: "success",
  pending: "pending",
  info: "info",
  warning: "warning",
  error: "error",
  cancelled: "cancelled",
};

/** Theme-aligned tone colors (portal design tokens). */
export const TIMELINE_TONE_COLORS = {
  [TIMELINE_TONES.success]: "#19bc31",
  [TIMELINE_TONES.pending]: "#ff9500",
  [TIMELINE_TONES.info]: "#31778c",
  [TIMELINE_TONES.warning]: "#da6900",
  [TIMELINE_TONES.error]: "#ff4343",
  [TIMELINE_TONES.cancelled]: "#A1A0A0",
};

export function toneColor(tone) {
  return TIMELINE_TONE_COLORS[tone] || TIMELINE_TONE_COLORS.info;
}

/**
 * Normalize a raw API timeline row (canonical or legacy) into a stable UI shape.
 */
export function normalizeTimelineItem(raw, index = 0) {
  if (!raw || typeof raw !== "object") return null;

  const status = raw.status || raw.to_status || null;
  const occurredAt = raw.occurred_at || raw.at || raw.created_at || null;
  const description =
    raw.description || raw.message || raw.note || raw.reason || "";
  const actorName =
    raw.actor?.name ||
    raw.staffName ||
    raw.staff_name ||
    raw.by ||
    (raw.changed_by_type
      ? String(raw.changed_by_type).replace(/_/g, " ")
      : null);

  return {
    id: String(raw.id || `${raw.event_key || status || "item"}-${occurredAt || index}`),
    eventKey: raw.event_key || raw.eventKey || null,
    category: raw.category || null,
    title: raw.title || null,
    description,
    status,
    visibility: raw.visibility || "customer",
    actor: raw.actor || (actorName ? { type: "unknown", id: null, name: actorName } : null),
    subject: raw.subject || null,
    metadata: raw.metadata && typeof raw.metadata === "object" ? raw.metadata : {},
    occurredAt,
    iconKey: raw.icon_key || raw.iconKey || null,
    tone: raw.tone || inferToneFromStatus(status),
    isTerminal: Boolean(raw.is_terminal ?? raw.isTerminal),
    // legacy aliases for thin wrappers
    at: occurredAt,
    message: description,
    note: description,
    staffName: actorName,
    by: actorName,
    reason: description,
  };
}

export function normalizeTimelineItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeTimelineItem).filter(Boolean);
}

export function inferToneFromStatus(status) {
  const s = String(status || "").toLowerCase();
  if (["approved", "allocated", "completed", "delivered", "confirmed", "matured"].includes(s)) {
    return TIMELINE_TONES.success;
  }
  if (["declined", "rejected", "failed"].includes(s)) return TIMELINE_TONES.error;
  if (["cancelled", "canceled"].includes(s)) return TIMELINE_TONES.cancelled;
  if (["pending", "pending_payment", "under_review", "awaiting_confirmation", "awaiting_contact", "awaiting_customer"].includes(s)) {
    return TIMELINE_TONES.pending;
  }
  if (["resubmission_required", "rescheduled"].includes(s)) return TIMELINE_TONES.warning;
  return TIMELINE_TONES.info;
}

/**
 * Group items by relative date buckets (newest groups first).
 */
export function groupTimelineByDate(items, now = new Date()) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 6);

  const buckets = {
    today: [],
    yesterday: [],
    earlier_this_week: [],
    older: [],
  };

  for (const item of items) {
    const d = item.occurredAt ? new Date(item.occurredAt) : null;
    if (!d || Number.isNaN(d.getTime())) {
      buckets.older.push(item);
      continue;
    }
    if (d >= startOfToday) buckets.today.push(item);
    else if (d >= startOfYesterday) buckets.yesterday.push(item);
    else if (d >= startOfWeek) buckets.earlier_this_week.push(item);
    else buckets.older.push(item);
  }

  const labels = {
    today: "Today",
    yesterday: "Yesterday",
    earlier_this_week: "Earlier this week",
    older: "Earlier",
  };

  return Object.entries(buckets)
    .filter(([, rows]) => rows.length > 0)
    .map(([key, rows]) => ({ key, label: labels[key], items: rows }));
}
